define('viewModels/privileged/queues/QueuesAnalyticsListView',[
    'lib/lodash4',
    'lib/knockout',
    'lib/bluebird',
    'moment',

    'models/AnalyticsData',

    'services/analyticsService',
    'services/notificationService',
    'services/queuesService',
    'services/intervalService',
    'services/analytics/analyticsCalculations',
    'services/analytics/analyticsFormatter',

    'viewModels/ViewModel',
    'viewModels/widgets/MediaTypePickerView',
    'viewModels/privileged/queues/dataControllers/QueueAnalyticsDataController',

    'util/errorCallbackFactory'
], function (
    _,
    ko,
    Promise,
    moment,

    AnalyticsData,

    analyticsService,
    notificationService,
    queuesService,
    intervalService,
    analyticsCalculations,
    analyticsFormatter,

    ViewModel,
    MediaTypePickerView,
    QueueAnalyticsDataController,

    errorCallbackFactory
) {
    'use strict';

    var QueueAnalyticsRowView = ViewModel.extend(function (queueObj) {
        var self = this;

        self.queue = ko.observable(queueObj);

        self.label = self.computed(function () {
            if (self.queue()) {
                return self.queue().name();
            }

            return '';
        });

        self.href = ko.observable("#queues/" + self.queue().id + "/agents");

        self.computed(function () {
            if (self.queue()) {
                self.href("#/analytics/queues/" + self.queue().id + "/performance");
            }
        });

        self.analyticsData = ko.observable();

        self.serviceLevelDeviation = self.computed(function () {
            if (!_.hasValue(self.analyticsData())) {
                return null;
            }

            var serviceLevelObj = self.analyticsData().getMetricObjectForInterval(
                analyticsService.STAT_KEYS.SERVICE_LEVEL,
                intervalService.currentInterval.peek()
            );

            if (_.isNull(serviceLevelObj) || !_.hasValue(serviceLevelObj.stats)) {
                return null;
            }

            var serviceLevel = serviceLevelObj.stats.ratio;
            var target = serviceLevelObj.stats.target;

            return (serviceLevel - target) * 100;
        });

        self.callsAbandonedRate = self.computed(function () {
            var abandonRate = analyticsCalculations.computeAbandonRate(self.analyticsData());

            if (_.isNull(abandonRate) || _.isUndefined(abandonRate)) {
                return null;
            }

            abandonRate = abandonRate * 100;

            return abandonRate;
        });

        self.avgSpeedAnswer = self.computed(function () {
            var avgAsa = null;

            if (self.analyticsData()) {
                avgAsa = self.analyticsData().avg(analyticsService.STAT_KEYS.ANSWERED);
            }

            if (!_.isNull(avgAsa)) {
                avgAsa = moment.duration(avgAsa).asSeconds();
            }

            return avgAsa;
        });
    });

    var QueueAnalyticsListView = ViewModel.extend(function (cachedQueuesService) {
        var self = this;

        //
        // Private Variables
        //

        var dataController = self.subView(QueueAnalyticsDataController);
        var pageData = {
            pageNumber: ko.observable(1),
            pageCount: ko.observable()
        };

        var isLoadingNextPage = ko.observable(false); // determine when the next page of queues has been fully loaded.
        var hasRows = ko.observable(false); // determine when the first set of rows have been loaded and created.

        //
        // Private Methods
        //

        /**
         * Load the Queue entities from the backend for the given page number. This method
         * will take the returned entities and create new row views for display to the end
         * user.
         *
         * @param  {Integer} pageNumber The page number to indicate which page of queues
         *  to load from the backend.
         * @return {Promise -> String[]} A promise that will pass on a collection of IDs
         *  of each queue returned from the backend. This is meant to help with loading
         *  analytics data by chaining to the returned Promise.
         */
        var loadQueuesForPageNumber = function (pageNumber) {
            var promise;
            if (cachedQueuesService && cachedQueuesService.getQueues) {
                promise = cachedQueuesService.getQueues({ page: pageNumber });
            } else {
                promise = queuesService.getQueues({
                    pageNumber: pageNumber
                });
            }
            return promise
                .then(function(response) {
                    pageData.pageCount(response.pageCount);

                    var newRowViews = [];
                    var newQueueIds = [];

                    _.forEach(response.entities, function (queue) {
                        newRowViews.push(self.subView(QueueAnalyticsRowView, queue));
                        newQueueIds.push(queue.id);
                    });

                    self.rowViews.push.apply(self.rowViews, newRowViews);

                    return newQueueIds;
                })
                .catch(errorCallbackFactory.createCallback());
        };

        var onNewAnalyticsDataForQueue = function (newDataModel) {
            var rowView = _.find(self.rowViews(), function (view) {
                return view.queue().id === newDataModel.entityId();
            });

            if (rowView) {
                rowView.analyticsData(newDataModel);
            }
        };

        var loadAnalyticsDataForQueueIds = function (idArr) {
            return dataController.getLiveAnalyticsDataForQueues(
                idArr,
                intervalService.currentInterval(),
                onNewAnalyticsDataForQueue
            )
                .then(function (analyticsModelCollection) {
                    _.each(analyticsModelCollection, function (queueMetric) {
                        var queueId = queueMetric.entityId();
                        var rowView = _.find(self.rowViews(), function (view) {
                            return view.queue().id === queueId;
                        });

                        if (rowView) {
                            rowView.analyticsData(queueMetric);
                        }
                    });
                })
                .catch(errorCallbackFactory.createCallback());
        };

        var resetAnalyticsData = function () {
            _.each(self.rowViews.peek(), function (rowView) {
                rowView.analyticsData(null);
            });
        };

        //
        // Template Data
        //

        self.isLoading = ko.observable(true);

        self.mediaTypePickerView = self.subView(MediaTypePickerView);

        self.rowViews = ko.observableArray();

        //
        // Private Computeds
        //

        var queueIdsArr = self.computed(function () {
            return _.map(self.rowViews(), function (view) {
                return view.queue().id;
            });
        });

        //
        // Events
        //

        self.formatPercentage = function (percentage) {
            return ko.i18n.format(percentage, "n2");
        };

        self.loadNextPage = function () {
            if (!isLoadingNextPage()) {
                isLoadingNextPage(true);

                var currentPage = pageData.pageNumber();
                if (currentPage < pageData.pageCount()) {
                    pageData.pageNumber(currentPage + 1);

                loadQueuesForPageNumber(pageData.pageNumber())
                    .then(function (newQueueIds) {
                        return loadAnalyticsDataForQueueIds(newQueueIds);
                    })
                    .finally(function () {
                        isLoadingNextPage(false);
                    });
                }
            }
        };

        //
        // Computeds
        //

        self.computed(function () {
            // Reload the analytics data if the media type has changed or the interval has
            // rolled over.
            //
            // This logic is contained in a computed instead of down in the initilization
            // stage in order to only have the analytics loading occur once on page load
            // and to register this computed to execute every time the interval or media
            // type changes.
            if (hasRows()) {
                resetAnalyticsData();
                self.isLoading(true);

                dataController.mediaType(self.mediaTypePickerView.selectedMediaTypeValue());

                loadAnalyticsDataForQueueIds(queueIdsArr.peek())
                    .finally(function () {
                        self.isLoading(false);
                    });
            }
        });

        //
        // Initialization
        //

        loadQueuesForPageNumber(pageData.pageNumber())
            .then(function () {
                hasRows(true);
            });
    });

    return QueueAnalyticsListView;
});

